/* eslint-disable @typescript-eslint/no-unused-vars */
import Table from 'components/Table';
import {
  focusableTableCellClassName,
  tableCellClassName,
} from 'components/Table/utils';
import useTranslations from 'contexts/basicData/useTranslations';
import React, { useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import useCompanies from 'contexts/basicData/useCompanies';
import { arraySpreadIf } from 'utils/spreading';
import useUnclosedCasesListColumnSettings, {
  ColumnProps as CaseListColumnProps,
} from './useUnclosedCasesListColumnSettings';
import useActiveColumns, {
  TableIdentifierEnum,
} from 'contexts/basicData/useActiveColumns';
import useAssignmentSearch from 'contexts/assignmentSearch/useAssignmentSearch';
import { AssignmentViewModel, BasicCaseModel } from 'api/model';

export const DotSpan = styled.span<{ dotColor?: string }>`
  height: 12px;
  width: 12px;
  background-color: ${({ dotColor }) =>
    dotColor ? `#${dotColor}` : 'transparent'};
  border-radius: 50%;
  display: inline-block;
  margin-right: 3px;
`;

const Center = styled.div`
  text-align: center;
`;

const assignmentIdRowClassName = (assignmentId: number | string): string =>
  `assignment-row-${assignmentId}`;

const rowClassNameMaker = (row: AssignmentViewModel): string =>
  assignmentIdRowClassName(row.assignment.assignmentID);

export const coloredRowClassNameMaker = (row: AssignmentViewModel): string =>
  [assignmentIdRowClassName(row.assignment.assignmentID)].join(' ');

const TableStylerWrapper = styled.div<{ savingAssignmentIds: string[] }>`
  display: contents;

  ${({ savingAssignmentIds }) =>
    savingAssignmentIds.map(
      (id) => css`
        .${assignmentIdRowClassName(id)} {
          animation: assignmentlist-save-animation 1s infinite;
        }
      `
    )}

  @keyframes assignmentlist-save-animation {
    0% {
      background: #ddeefd;
    }
    50% {
      background: ${({ theme }) => theme.colors.background.primary};
    }
    100% {
      background: #ddeefd;
    }
  }
`;

const MyTable = styled(Table)`
  flex: 1;

  /* when a cell is focused, allow overflow for UserPicker */
  ${`.${tableCellClassName}`}:focus-within {
    overflow: visible;
  }

  ${`.${focusableTableCellClassName}`}:focus-within {
    padding: 0;
  }
` as typeof Table;

interface Props {
  onCaseClick?(model: BasicCaseModel): void;
  cases: BasicCaseModel[];
  loading?: boolean;
  currentPage?: number;
  onGoToPage?(page: number): void;
  totalPageCount?: number;
  pageSize?: number;
  tableIdentifier?: TableIdentifierEnum;
}

const CasesList: React.FC<Props> = ({
  onCaseClick,
  cases,
  loading = false,
  currentPage,
  onGoToPage,
  totalPageCount,
  tableIdentifier = TableIdentifierEnum.CasesList,
  pageSize,
}) => {
  const [activeColumns] = useActiveColumns(tableIdentifier);
  const { setSortState: setAssignmentSearchSortState } = useAssignmentSearch();

  const columnSettings = useUnclosedCasesListColumnSettings();

  const filteredColumnSettings = useMemo(() => {
    if (!tableIdentifier) return columnSettings;
    return columnSettings.filter((col) => {
      // show the column if it either has not yet been toggled off, or if it is not hidden by default
      if (
        col.identifier === undefined ||
        activeColumns[col.identifier] === true ||
        (activeColumns[col.identifier] === undefined &&
          col.hideByDefault !== true)
      )
        return true;
      return false;
    });
  }, [activeColumns, columnSettings, tableIdentifier]);

  return (
    <TableStylerWrapper savingAssignmentIds={[]}>
      {cases.length === 0 ? (
        <Center>{loading ? 'Laddar' : 'Hittade inga ärenden'}</Center>
      ) : (
        <MyTable
          columnSettings={filteredColumnSettings}
          currentPage={currentPage}
          fluid
          onGoToPage={onGoToPage}
          initialSortState={{
            ascending: false,
            sortedColumnIndex: 1,
          }}
          onRowClick={onCaseClick}
          onSortStateChange={(sortState) => {
            setAssignmentSearchSortState(sortState);
          }}
          pageSize={pageSize}
          rows={cases}
          totalPageCount={totalPageCount}
        />
      )}
    </TableStylerWrapper>
  );
};

export default CasesList;
